import { tz } from "@date-fns/tz";
import { useDRPContext } from "@inputs/date/DateRangePicker/context";
import { rangeDateToString } from "@inputs/date/DateRangePicker/logics/date";
import type { IDRPHeaderForm } from "@inputs/date/DateRangePicker/types";
import { endOfYear, set, startOfYear } from "date-fns";
import { useCallback, useEffect } from "react";
import { type FieldErrorsImpl, type Resolver, useForm } from "react-hook-form";

const dateRegex = /^(\d{4})$/;
const dateFormatOptions: Intl.DateTimeFormatOptions = { year: "numeric" };

const parseDate = (value: string) => {
  const match = value.match(dateRegex);
  if (match) {
    const year = Number(match[1]) || 0;
    return year;
  }
  return null;
};

const resolver: Resolver<IDRPHeaderForm> = (values) => {
  const start = parseDate(values.startDate);
  const end = parseDate(values.endDate);

  const errors: Partial<FieldErrorsImpl<IDRPHeaderForm>> = {};
  if (start && end && (start > end || (start === end && start > end))) {
    return {
      values: {},
      errors: {
        startDate: { type: "validate", message: "La date de début ne peut pas être supérieure à la date de fin" },
      },
    };
  }

  if (errors.startDate ?? errors.endDate) {
    return { values: {}, errors };
  }
  return { values, errors: {} };
};

export const useDRYPHeader = () => {
  const { range, handleRangeChange, error, required, endDateDisabled, timeZone } = useDRPContext();
  const { reset, control, handleSubmit } = useForm<IDRPHeaderForm>({
    defaultValues: rangeDateToString(range, dateFormatOptions, timeZone),
    resolver,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    reset(rangeDateToString(range, dateFormatOptions, timeZone));
  }, [range]);

  const submit = useCallback(
    (values: IDRPHeaderForm) => {
      const start = parseDate(values.startDate);
      const end = parseDate(values.endDate);
      const tzCtx = { in: tz(timeZone) };

      handleRangeChange({
        startDate: start ? set(startOfYear(new Date(start, 1), tzCtx), { milliseconds: 2 }) : null,
        endDate: end ? set(endOfYear(new Date(end, 1), tzCtx), { milliseconds: 997 }) : null,
      });
    },
    [handleRangeChange, timeZone]
  );

  return { control, submit: handleSubmit(submit), error, required, endDateDisabled };
};
